import { useMemo } from "react";
import Form from "@rjsf/core";
import validator from "@rjsf/validator-ajv8";
import type { RJSFSchema, UiSchema } from "@rjsf/utils";
import { formatTokens } from "@/lib/utils";

export interface OutcomeFormProps {
  schema: RJSFSchema;
  uiSchema?: UiSchema;
  onSubmit: (data: Record<string, unknown>) => void | Promise<void>;
  submitting: boolean;
  priceTokens: number;
  maxPriceTokens?: number | null;
  priceKind: "fixed" | "variable";
  balance: number;
  isAuthenticated: boolean;
}

export function OutcomeForm({
  schema,
  uiSchema,
  onSubmit,
  submitting,
  priceTokens,
  maxPriceTokens,
  priceKind,
  balance,
  isAuthenticated,
}: OutcomeFormProps) {
  const effectiveCost = maxPriceTokens ?? priceTokens;
  const insufficient = isAuthenticated && balance < effectiveCost;

  const priceLabel = useMemo(() => {
    if (priceKind === "variable" && maxPriceTokens) {
      return `${formatTokens(priceTokens)}–${formatTokens(maxPriceTokens)}`;
    }
    return formatTokens(priceTokens);
  }, [priceKind, priceTokens, maxPriceTokens]);

  const runLabel = submitting
    ? "Starting…"
    : !isAuthenticated
      ? "Log in to run"
      : insufficient
        ? `Need ${formatTokens(effectiveCost - balance)} more tokens`
        : `Run (${priceLabel} tokens)`;

  return (
    <Form
      schema={schema}
      uiSchema={uiSchema}
      validator={validator}
      onSubmit={({ formData }) =>
        onSubmit((formData ?? {}) as Record<string, unknown>)
      }
      disabled={submitting || !isAuthenticated}
      showErrorList={false}
    >
      <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        {priceKind === "variable" && maxPriceTokens ? (
          <p className="text-xs text-neutral-500">
            Final price depends on your input. You'll be held for up to{" "}
            <span className="font-medium">{formatTokens(maxPriceTokens)}</span>{" "}
            tokens; any unused amount is refunded automatically.
          </p>
        ) : (
          <span />
        )}
        <button
          type="submit"
          disabled={submitting || !isAuthenticated || insufficient}
          className="rounded-lg bg-emerald-500 px-6 py-3 font-medium text-white transition hover:bg-emerald-600 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {runLabel}
        </button>
      </div>
    </Form>
  );
}
